import { useState, useEffect, useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogClose,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { Input } from "@/components/ui/input"
import { TruckIcon, BuildingIcon, ClockIcon, MessageSquareIcon } from "lucide-react"
import { BATEA_LOADING_WINDOWS } from "@cdc-sccr/shared"

// ─── Types ────────────────────────────────────────────────────────────────────

interface MobileOption {
  id:                string
  isActive:          boolean
  driverId:          string | null
  truckId:           string | null
  trailerId:         string | null
  driverName:        string | null
  truckLicensePlate: string | null
  truckUnitNumber:   number | null
  operatorName:      string | null
}

interface PartyOption {
  id:          string
  name:        string
  externalId:  string | null
  cityId:      string | null
  cityName:    string | null
  countryName: string | null
}

export interface QuickEditTarget {
  type:                "mobile" | "sender" | "recipient" | "loadingWindow" | "notes"
  shipmentId:          string
  internalId:          number
  firstTripId:         string | null
  currentMobileId:     string | null
  currentSenderId:     string | null
  currentRecipientId:  string | null
  currentLoadingWindow: string | null
  currentNotes:        string | null
}

interface Props {
  target:    QuickEditTarget | null
  onClose:   () => void
  onSuccess: () => void
}

// ─── Config per type ──────────────────────────────────────────────────────────

const LOADING_WINDOW_OPTIONS = [
  { value: "__none__", label: "Sin asignar" },
  ...BATEA_LOADING_WINDOWS.map(w => ({ value: w.slug, label: w.label })),
]

const TYPE_CONFIG = {
  mobile:        { icon: TruckIcon,           label: "Móvil",           description: "Reasigná el móvil del primer viaje. El chofer, camión y semi se actualizan automáticamente." },
  sender:        { icon: BuildingIcon,        label: "Remitente",        description: "Cambiá el remitente del envío." },
  recipient:     { icon: BuildingIcon,        label: "Destinatario",     description: "Cambiá el destinatario del envío." },
  loadingWindow: { icon: ClockIcon,           label: "Ventana de carga", description: "Seleccioná la ventana horaria de carga en origen." },
  notes:         { icon: MessageSquareIcon,   label: "Observaciones",    description: "Agregá o editá las observaciones de esta carga." },
}

// ─── Component ────────────────────────────────────────────────────────────────

export function QuickEditDialog({ target, onClose, onSuccess }: Props) {
  const qc = useQueryClient()
  const [value, setValue] = useState("")
  const [error, setError] = useState<string | null>(null)

  const isOpen = target !== null
  const type   = target?.type ?? "recipient"

  // Sync selection when target changes
  useEffect(() => {
    if (!target) return
    if (target.type === "mobile")         setValue(target.currentMobileId      ?? "")
    else if (target.type === "sender")    setValue(target.currentSenderId      ?? "")
    else if (target.type === "recipient") setValue(target.currentRecipientId   ?? "")
    else if (target.type === "notes")     setValue(target.currentNotes         ?? "")
    else                                  setValue(target.currentLoadingWindow  ?? "")
    setError(null)
  }, [target])

  const needsMobiles        = isOpen && type === "mobile"
  const needsPartiesForEdit = isOpen && (type === "sender" || type === "recipient")
  // Also load parties when assigning mobile to a trip-less shipment (to resolve city IDs)
  const needsPartiesForCity = isOpen && type === "mobile" && !target?.firstTripId
  const needsParties        = needsPartiesForEdit || needsPartiesForCity

  // Lazy-load mobiles only when editing mobile
  const { data: mobiles = [], isLoading: mobilesLoading } = useQuery<MobileOption[]>({
    queryKey: ["mobiles-select"],
    enabled:  needsMobiles,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/mobiles`)
      return res.ok ? (res.json() as Promise<MobileOption[]>) : []
    },
  })

  // Lazy-load parties only when editing sender or recipient
  const { data: parties = [], isLoading: partiesLoading } = useQuery<PartyOption[]>({
    queryKey: ["parties-select"],
    enabled:  needsParties,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/parties?deleted=false&page=1&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: PartyOption[] }
      return json.data
    },
  })

  const mobileOptions = useMemo(() => {
    function inactiveReason(m: MobileOption): string {
      const missing: string[] = []
      if (!m.driverId)  missing.push("sin chofer")
      if (!m.trailerId) missing.push("sin semi")
      return missing.length > 0 ? missing.join(", ") : "inactivo"
    }
    return [
      { value: "__none__", label: "Sin asignar" },
      ...mobiles.map(m => ({
        value:    m.id,
        label:    m.truckUnitNumber != null ? `M${m.truckUnitNumber}` : (m.truckLicensePlate ?? m.id),
        sublabel: m.isActive ? (m.driverName ?? undefined) : inactiveReason(m),
        keywords: [m.operatorName, m.driverName, m.truckLicensePlate].filter(Boolean).join(" "),
        disabled: !m.isActive,
      })),
    ]
  }, [mobiles])

  const partyOptions = useMemo(() => [
    { value: "__none__", label: "Sin asignar" },
    ...parties.map(p => ({
      value:    p.id,
      label:    p.name,
      sublabel: [p.externalId ? `ID: ${p.externalId}` : null, p.cityName, p.countryName].filter(Boolean).join(" / ") || undefined,
      keywords: p.externalId ?? undefined,
    })),
  ], [parties])

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error("Sin envío seleccionado")
      const cleanValue = (value === "__none__" || value === "") ? null : value

      if (target.type === "mobile") {
        const mobile = cleanValue ? mobiles.find(m => m.id === cleanValue) : null
        if (target.firstTripId) {
          // Update existing trip
          await authorizedFetch(`${API_URL}/api/trips/${target.firstTripId}`, {
            method:  "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mobileId:  cleanValue,
              driverId:  mobile?.driverId  ?? null,
              truckId:   mobile?.truckId   ?? null,
              trailerId: mobile?.trailerId ?? null,
            }),
          })
        } else {
          // No trip yet — create one
          if (!cleanValue) throw new Error("Seleccioná un móvil para crear el viaje")
          const sender    = parties.find(p => p.id === target.currentSenderId)
          const recipient = parties.find(p => p.id === target.currentRecipientId)
          await authorizedFetch(`${API_URL}/api/trips`, {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              shipmentId:        target.shipmentId,
              mobileId:          cleanValue,
              driverId:          mobile?.driverId  ?? null,
              truckId:           mobile?.truckId   ?? null,
              trailerId:         mobile?.trailerId ?? null,
              originCityId:      sender?.cityId    ?? null,
              destinationCityId: recipient?.cityId ?? null,
            }),
          })
        }
      } else if (target.type === "sender") {
        await authorizedFetch(`${API_URL}/api/shipments/${target.shipmentId}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ senderId: cleanValue }),
        })
      } else if (target.type === "recipient") {
        await authorizedFetch(`${API_URL}/api/shipments/${target.shipmentId}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recipientId: cleanValue }),
        })
      } else if (target.type === "notes") {
        await authorizedFetch(`${API_URL}/api/shipments/${target.shipmentId}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes: cleanValue }),
        })
      } else {
        await authorizedFetch(`${API_URL}/api/shipments/${target.shipmentId}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ loadingWindow: cleanValue }),
        })
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipments"] })
      if (type === "mobile") qc.invalidateQueries({ queryKey: ["trips"] })
      onSuccess()
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const cfg       = TYPE_CONFIG[type]
  const Icon      = cfg.icon
  const isLoading = type === "mobile" ? mobilesLoading : needsParties ? partiesLoading : false

  function renderSelect() {
    if (type === "notes") {
      return (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Sin observaciones"
          rows={4}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
        />
      )
    }
    if (type === "mobile") {
      return (
        <SearchableSelect
          options={mobileOptions}
          value={value || "__none__"}
          onValueChange={setValue}
          placeholder={isLoading ? "Cargando..." : "Sin asignar"}
          searchPlaceholder="Buscar por N° móvil, patente o chofer..."
          disabled={isLoading}
        />
      )
    }
    if (type === "sender" || type === "recipient") {
      return (
        <SearchableSelect
          options={partyOptions}
          value={value || "__none__"}
          onValueChange={setValue}
          placeholder={isLoading ? "Cargando..." : "Sin asignar"}
          searchPlaceholder={`Buscar ${type === "sender" ? "remitente" : "destinatario"}...`}
          disabled={isLoading}
        />
      )
    }
    // loadingWindow — static options, no fetch needed
    return (
      <SearchableSelect
        options={LOADING_WINDOW_OPTIONS}
        value={value || "__none__"}
        onValueChange={setValue}
        placeholder="Sin asignar"
        searchPlaceholder="Buscar ventana..."
      />
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="size-4 text-muted-foreground" />
            {cfg.label}
            <span className="font-normal text-muted-foreground">
              — Carga #{target?.internalId}
            </span>
          </DialogTitle>
          <DialogDescription>{cfg.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <div className="grid gap-1.5">
            <Label>{cfg.label}</Label>
            {renderSelect()}
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
          )}
        </div>

        <DialogFooter>
          <DialogClose>
            <Button type="button" variant="outline">Cancelar</Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || isLoading}
          >
            {saveMut.isPending ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
